export interface TurnstileApi {
	render(container: HTMLElement, options: {
		sitekey: string;
		language: string;
		retry: "never";
		callback: (token: string) => void;
		"error-callback": (code: string) => void;
		"expired-callback": () => void;
		"timeout-callback": () => void;
	}): string;
	remove(widgetId: string): void;
}

declare global {
	interface Window { turnstile?: TurnstileApi }
}

export type TurnstileFailure = "script" | "script_timeout" | "config_request" | "config_missing" | "challenge" | "expired" | "timeout";
export class TurnstileError extends Error {
	constructor(readonly reason: TurnstileFailure) { super(reason); }
}

export const TURNSTILE_MESSAGES: Record<TurnstileFailure, string> = {
	script: "확인 도구를 받지 못했어요. 연결이나 콘텐츠 차단 설정을 확인한 뒤 다시 시도해 주세요. 쓴 내용은 그대로예요.",
	script_timeout: "확인 도구를 기다리는 시간이 길어졌어요. 연결을 확인하고 다시 시도해 주세요. 쓴 내용은 그대로예요.",
	config_request: "서비스의 확인 설정을 불러오지 못했어요. 잠시 뒤 다시 시도해 주세요. 쓴 내용은 그대로예요.",
	config_missing: "서비스의 사람 확인 설정이 준비되지 않았어요. 지금은 공개할 수 없어요. 쓴 내용은 그대로예요.",
	challenge: "사람 확인을 마치지 못했어요. 다시 확인해 주세요. 쓴 내용은 그대로예요.",
	expired: "사람 확인 시간이 지났어요. 다시 확인하면 공개할 수 있어요. 쓴 내용은 그대로예요.",
	timeout: "사람 확인 시간이 길어졌어요. 다시 확인해 주세요. 쓴 내용은 그대로예요.",
};

let scriptPromise: Promise<void> | null = null;

/** One concurrent load; a failed or stalled script never poisons the next attempt. */
export function loadTurnstile(): Promise<void> {
	if (window.turnstile) return Promise.resolve();
	if (scriptPromise) return scriptPromise;
	const pending = new Promise<void>((resolve, reject) => {
		const script = document.createElement("script");
		const finish = (reason?: TurnstileFailure) => {
			clearTimeout(timer);
			script.onload = null;
			script.onerror = null;
			if (reason) { script.remove(); reject(new TurnstileError(reason)); }
			else resolve();
		};
		const timer = setTimeout(() => finish("script_timeout"), 15_000);
		script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
		script.async = true;
		script.defer = true;
		script.onload = () => finish(window.turnstile ? undefined : "script");
		script.onerror = () => finish("script");
		document.head.appendChild(script);
	});
	scriptPromise = pending;
	void pending.catch(() => { if (scriptPromise === pending) scriptPromise = null; });
	return pending;
}

export async function loadTurnstileSiteKey(signal: AbortSignal): Promise<string> {
	const controller = new AbortController();
	const cancel = () => controller.abort();
	if (signal.aborted) cancel();
	signal.addEventListener("abort", cancel, { once: true });
	const timer = setTimeout(cancel, 10_000);
	try {
		const response = await fetch("/api/runtime-config", { signal: controller.signal, cache: "no-store" });
		if (!response.ok) throw new TurnstileError("config_request");
		const config: unknown = await response.json();
		if (!config || typeof config !== "object" || !("turnstileSiteKey" in config)) throw new TurnstileError("config_request");
		if (typeof config.turnstileSiteKey !== "string" || !config.turnstileSiteKey.trim()) throw new TurnstileError("config_missing");
		return config.turnstileSiteKey;
	} catch (error) {
		throw error instanceof TurnstileError ? error : new TurnstileError("config_request");
	} finally {
		clearTimeout(timer);
		signal.removeEventListener("abort", cancel);
	}
}
